// @flow @jsx h
import { Component, h } from 'preact'
import { ViewportIntersections, HtmlContent } from '@nrk/dh-feature-components'
import { connect } from '../../../../lib/provider'
import { Person } from './Person'
import { Faktaboks } from './Faktaboks'
import { Image } from './Image'
import { VideoElement } from './VideoElement'
import { LudoElement } from './LudoElement'
import { Sitat } from './Sitat'
import { LevendeStillbilde } from './LevendeStillbilde'
import { Inngang } from './Inngang'
import { Lydtekst } from './Lydtekst'
import { BildeLydtekst } from './BildeLydtekst'

/* styles */
import styles from './index.css'

/* types */
import type { TextType } from '../../domain'

type Props = {
  tekst: TextType[],
  blockId: number,
  sendGaEvent: (Object) => void
}

type State = {
  loadContent: any,
  visibleContent: any,
  fullyVisibleContent: any
}

const LOADABLE_CONTENT = ['image', 'inngang', 'bildelydtekst']
const VISIBLE_CONTENT = ['levendeStillbilde']
const FULLY_VISIBLE_CONTENT = ['video', 'ludo']

class _TekstBlokk extends Component<Props, State> {
  trackedKeys = {}

  constructor () {
    super()
    this.state = {
      loadContent: [],
      visibleContent: [],
      fullyVisibleContent: []
    }
  }

  getContenttype (idx: number) {
    const contentList = this.props.tekst
    if (idx < 0 || idx >= contentList.length) {
      return null
    } else {
      return contentList[idx].type
    }
  }

  ensureLoaded (idx: number) {
    const type = this.getContenttype(idx)
    if (LOADABLE_CONTENT.indexOf(type) !== -1) {
      const loadContent = this.state.loadContent
      if (!loadContent[idx]) {
        loadContent[idx] = true
        this.setState({ loadContent })
      }
    }
  }

  trackOnlyOnce (key: string, action: string, label: string) {
    if (!this.trackedKeys[key]) {
      this.trackedKeys[key] = true
      this.props.sendGaEvent && this.props.sendGaEvent({ action, label })
    }
  }

  handleContentEnter = (element: any, idx: number) => {
    this.ensureLoaded(idx - 2)
    this.ensureLoaded(idx - 1)
    this.ensureLoaded(idx)
    this.ensureLoaded(idx + 1)
    this.ensureLoaded(idx + 2)

    const contentList = this.props.tekst
    const content = contentList[idx]

    if (VISIBLE_CONTENT.indexOf(content.type) !== -1) {
      const visibleContent = this.state.visibleContent
      if (!visibleContent[idx]) {
        visibleContent[idx] = true
        this.setState({ visibleContent })
      }
    }

    const key = this.props.blockId + '-' + idx
    this.trackOnlyOnce(key, 'SHOW_CONTENT', `${key}/${content.type}`)
  }

  handleContentLeave = (element: any, idx: number) => {
    const contentList = this.props.tekst
    const content = contentList[idx]

    if (VISIBLE_CONTENT.indexOf(content.type) !== -1) {
      const visibleContent = this.state.visibleContent
      if (visibleContent[idx]) {
        visibleContent[idx] = false
        this.setState({ visibleContent })
      }
    }
  }

  handleContentChange = (intersections: any[]) => {
    const { fullyVisibleContent } = this.state
    const contentList = this.props.tekst
    let change = false

    intersections.forEach((block, idx) => {
      const content = contentList[idx]
      if (FULLY_VISIBLE_CONTENT.indexOf(content.type) === -1) {
        return
      }

      const isVisible = !!fullyVisibleContent[idx]
      if (isVisible && block.ratio < 0.05) {
        fullyVisibleContent[idx] = false
        change = true
      } else if (!isVisible && block.isIntersecting && block.ratio > 0.95) {
        fullyVisibleContent[idx] = true
        change = true
      }
    })

    if (change) {
      this.setState({ fullyVisibleContent })
    }
  }

  render () {
    return <ViewportIntersections
      className={styles.tekstBlokk}
      onEnter={this.handleContentEnter}
      onLeave={this.handleContentLeave}
      onChange={this.handleContentChange}
      threshold={[0, 1]}>
      {
        this.props.tekst.map((element, idx) => {
          switch (element.type) {
            case 'text':
              return <HtmlContent
                key={idx}
                type='p'
                content={element.value} />
            case 'overskrift':
              return <h2>{element.value}</h2>
            case 'image':
              return <Image
                key={idx}
                id={element.value.polopolyId}
                alt={element.value.alt}
                caption={element.value.caption}
                ratio={element.value.ratio}
                load={this.state.loadContent[idx]}
              />
            case 'video':
              const { poster, mp4, webm } = element.value
              return <VideoElement
                key={idx}
                playing={this.state.fullyVisibleContent[idx]}
                poster={poster}
                mp4={mp4}
                webm={webm} />
            case 'ludo':
              return <LudoElement
                key={idx}
                id={element.value.id}
                autoplay={element.value.autoplay}
                playing={this.state.fullyVisibleContent[idx]}
              />
            case 'person':
              return <Person
                key={idx}
                intro={element.value.intro}
                linkText={element.value.linkText}
                imageId={element.value.imageId}
                title={element.value.title}
                text={element.value.text} />
            case 'faktaboks':
              return <Faktaboks
                key={idx}
                intro={element.value.intro}
                linkText={element.value.linkText}
                title={element.value.title}
                text={element.value.text} />
            case 'sitat':
              return <Sitat
                sitat={element.value.sitat}
                sitatNavn={element.value.sitatNavn}
                float={element.value.float}
              />
            case 'levendeStillbilde':
              return <LevendeStillbilde
                key={idx}
                playing={this.state.visibleContent[idx]}
                video={element.value.video}
                title={element.value.alt}
                caption={element.value.caption}
              />
            case 'bildelydtekst':
              return <BildeLydtekst
                key={idx}
                id={element.value.polopolyId}
                alt={element.value.alt}
                mp3={element.value.mp3}
                captions={element.value.captions}
                load={this.state.loadContent[idx]}
              />
            case 'lydtekst':
              return <Lydtekst
                key={idx}
                mp3={element.value.mp3}
                captions={element.value.captions}
              />
            case 'inngang':
              return <Inngang
                key={idx}
                polopolyId={element.value.polopolyId}
                tittel={element.value.tittel}
                merkelapp={element.value.merkelapp}
                url={element.value.url}
                ratio={element.value.ratio}
                load={this.state.loadContent[idx]} />
            default:
              return <pre key={idx} style={{ overflow: 'scroll' }}>
                {JSON.stringify(element, null, 2)}
              </pre>
          }
        })

      }
    </ViewportIntersections>
  }
}

const connector = connect()
// const connector = connect(mapContextToProps)
export const TekstBlokk = connector(_TekstBlokk)
